/**
 * Detail of a non-ride (run, swim, gym...): one summary card, no chart,
 * zones, best efforts or coach summary (docs/ACTIVITIES_PLAN.md §4).
 */
import { useTranslation } from 'react-i18next';
import { StravaLink } from './StravaLink.tsx';
import {
  formatDistanceKm,
  formatDuration,
  formatElevation,
  formatRideDate,
} from '../lib/rideFormat.ts';
import { activitySource, showsDistance, sportIcon, sportNameKey } from '../lib/sports.ts';
import type { RideListItem } from '../types/api.ts';

function bpm(value: number | null): string {
  return value == null ? '—' : `${value} bpm`;
}

function summaryRows(
  item: RideListItem,
  t: (key: string) => string,
): { label: string; value: string }[] {
  return [
    { label: t('activities.date'), value: formatRideDate(item.started_at) },
    { label: t('rideDetail.stats.duration'), value: formatDuration(item.duration_sec) },
    ...(showsDistance(item)
      ? [{ label: t('rideDetail.stats.distance'), value: formatDistanceKm(item.distance_m) }]
      : []),
    { label: t('rideDetail.stats.elevation'), value: formatElevation(item.elevation_gain_m) },
    { label: t('rideDetail.stats.avgHr'), value: bpm(item.avg_hr) },
    { label: t('rideDetail.stats.maxHr'), value: bpm(item.max_hr) },
    { label: t('rideDetail.stats.calories'), value: item.calories == null ? '—' : `${item.calories}` },
    { label: t('activities.source'), value: activitySource(item, t) },
  ];
}

export function ActivitySummary({ item }: Readonly<{ item: RideListItem }>) {
  const { t } = useTranslation();
  const sportName = t(sportNameKey(item.sport));
  return (
    <>
      <section
        aria-label={t('activities.summaryTitle')}
        data-testid="activity-summary"
        className="rounded-lg bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3"
      >
        <div className="flex items-center gap-2">
          <span className="text-2xl" aria-hidden="true">{sportIcon(item)}</span>
          <h2 className="text-base font-semibold text-(--color-text-primary)">{sportName}</h2>
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {summaryRows(item, t).map((row) => (
            <div
              key={row.label}
              className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) p-3 text-center"
            >
              <dt className="text-xs text-(--color-text-secondary) mb-1">{row.label}</dt>
              <dd className="text-sm font-semibold text-(--color-text-primary)">{row.value}</dd>
            </div>
          ))}
        </dl>
      </section>
      <StravaLink ride={item} />
    </>
  );
}
